import { ElementRef, Input, Directive, OnDestroy, OnInit } from '@angular/core';
import { merge, concat, of, Subscription, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';

@UntilDestroy()
@Directive({
  selector: '[nimbusShowMore]',
  standalone: false,
})
export class NimbusShowMoreDirective implements OnInit, OnDestroy {
  @Input() public showMoreClass = 'show-more';
  @Input() public showMorePanelClass = 'show-more-panel';
  @Input() public showMorePanelContainerClass = 'show-more-panel-container';
  @Input('nimbusShowMore') public componentChanges$: Observable<void>;

  private readonly showMoreElement: HTMLElement;
  private panelElement: HTMLElement;
  private panelContainerElement: HTMLElement;

  private subscription$: Subscription;

  public constructor(
    currentElementRef: ElementRef,
    private readonly windowEventsService: WindowEventsService,
  ) {
    this.showMoreElement = currentElementRef.nativeElement;
  }

  public ngOnInit(): void {
    this.panelElement = this.findParentByClass(this.showMoreElement, this.showMorePanelClass);
    if (!this.panelElement) {
      throw new Error(`The element with class ${this.showMorePanelClass} not found.`);
    }

    this.panelContainerElement = this.findParentByClass(this.panelElement, this.showMorePanelContainerClass);
    if (!this.panelContainerElement) {
      throw new Error(`The element with class ${this.showMorePanelContainerClass} not found.`);
    }

    this.setupSubscription();
  }

  public ngOnDestroy(): void {
    if (this.subscription$) {
      this.subscription$.unsubscribe();
    }
  }

  private findParentByClass(element: HTMLElement, className: string): HTMLElement | null {
    let parent: HTMLElement = element;

    while (!(parent = parent.parentElement).className.includes(className)) {
      if (!parent.parentElement) {
        return null;
      }
    }

    return parent;
  }

  private setupSubscription(): void {
    const sizeChanges$ = concat(of(undefined), merge(this.componentChanges$, this.windowEventsService.getResize$()));

    this.subscription$ = sizeChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.panelElement.clientHeight > this.panelContainerElement.clientHeight) {
        if (this.showMoreElement.style.display === 'none') {
          this.showMoreElement.style.display = '';
        }
      } else {
        if (this.showMoreElement.style.display !== 'none') {
          this.showMoreElement.style.display = 'none';
        }
      }
    });
  }
}
